import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  private audio: HTMLAudioElement | null = null;
  private audioReady = false;
  private isPlaying = false;
  private playPromise: Promise<void> | null = null;
  private audioElements: HTMLAudioElement[] = []; // Rastrear todos los elementos

  constructor() {
    this.prepareAudio();
    this.enableAudioOnFirstInteraction();
  }

  private enableAudioOnFirstInteraction(): void {
    const enableOnce = () => {
      if (this.audio && !this.audioReady) {
        this.audio.play()
          .then(() => {
            this.audio!.pause();
            this.audio!.currentTime = 0;
            this.audioReady = true;
            console.log('✅ AUDIO: Audio listo tras interacción del usuario');
          })
          .catch(() => console.log('⚠️ AUDIO: Aún no disponible'));
      }
      document.removeEventListener('click', enableOnce);
      document.removeEventListener('keydown', enableOnce);
    };

    document.addEventListener('click', enableOnce, { once: true });
    document.addEventListener('keydown', enableOnce, { once: true });
  }

  private prepareAudio(): void {
    try {
      // Primero intentar obtener el elemento del DOM
      const domAudio = document.getElementById('alarm-player') as HTMLAudioElement;
      if (domAudio) {
        this.audio = domAudio;
        console.log('✅ AUDIO: Usando elemento de audio del DOM');
      } else {
        // Fallback: crear el audio programáticamente
        this.audio = new Audio('assets/sounds/alarm.mp3');
        this.audio.id = 'alarm-player';
        this.audio.loop = true;
        this.audio.volume = 0.8;
        this.audio.preload = 'auto';
        document.body.appendChild(this.audio);
        console.log('⚠️ AUDIO: Creando elemento de audio programáticamente');
      }

      // Agregar a la lista de elementos
      this.audioElements.push(this.audio);

      // Agregar event listeners para monitorear el estado
      this.audio.addEventListener('play', () => {
        this.isPlaying = true;
        this.audioReady = true;
        console.log('🔊 AUDIO: Audio iniciado');
      });

      this.audio.addEventListener('pause', () => {
        this.isPlaying = false;
        console.log('🔇 AUDIO: Audio pausado');
      });

      this.audio.addEventListener('ended', () => {
        this.isPlaying = false;
        console.log('🔇 AUDIO: Audio terminado');
      });

      this.audio.addEventListener('error', (e) => {
        console.error('❌ AUDIO: Error en elemento de audio:', e);
        this.isPlaying = false;
      });

    } catch (error) {
      console.error('Error preparando audio:', error);
    }
  }


  playAlarm(): void {
    console.log('🔊 AUDIO: Intentando reproducir alarma');
    console.log('🔊 AUDIO: Estado actual - isPlaying:', this.isPlaying);

    // Si ya está sonando, no hacer nada
    if (this.isPlaying) {
      console.log('🔊 AUDIO: Alarma ya está sonando, omitiendo');
      return;
    }

    if (!this.audio) {
      console.log('🔊 AUDIO: Preparando audio');
      this.prepareAudio();
    }

    if (this.audio) {
      console.log('🔊 AUDIO: Audio disponible, reproduciendo');
      this.audio.loop = true;
      this.audio.currentTime = 0;

      this.playPromise = this.audio.play();
      this.playPromise.then(() => {
        this.isPlaying = true;
        this.audioReady = true;
        console.log('✅ AUDIO: Alarma reproduciéndose correctamente');
      }).catch(error => {
        console.warn('🔇 AUDIO: No se puede reproducir audio:', error.message);
        this.isPlaying = false;
        this.showVisualAlert();
      });
    } else {
      console.error('❌ AUDIO: No se pudo crear el elemento de audio');
    }
  }

  private showVisualAlert(): void {
    // Crear notificación visual como fallback
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('¡ALERTA DE PÁNICO!', {
        body: 'Se ha activado una alerta de pánico',
        icon: '/favicon.ico',
        requireInteraction: true
      });
    }
  }

  stopAlarm(): void {
    console.log('🔇 AUDIO: Deteniendo alarma');
    console.log('🔇 AUDIO: Estado actual - isPlaying:', this.isPlaying);

    if (this.audio) {
      try {
        // Desabilitar loop inmediatamente
        this.audio.loop = false;

        // Esperar a que termine cualquier operación de play pendiente
        if (this.playPromise) {
          this.playPromise.then(() => {
            if (this.audio) {
              this.audio.pause();
              this.audio.currentTime = 0;
              this.isPlaying = false;
              console.log('✅ AUDIO: Alarma detenida (async)');
            }
          }).catch(() => {
            // Si hay error en play, aún así intentar detener
            if (this.audio) {
              this.audio.pause();
              this.audio.currentTime = 0;
              this.isPlaying = false;
              console.log('✅ AUDIO: Alarma detenida (catch)');
            }
          });
        } else {
          // No hay promesa pendiente, detener directamente
          this.audio.pause();
          this.audio.currentTime = 0;
          this.isPlaying = false;
          console.log('✅ AUDIO: Alarma detenida (direct)');
        }
      } catch (error) {
        console.error('❌ AUDIO: Error deteniendo alarma:', error);
        this.isPlaying = false;
      }
    } else {
      console.log('⚠️ AUDIO: No hay elemento de audio para detener');
      this.isPlaying = false;
    }
  }
  
  // Método para verificar si está sonando
  isAlarmPlaying(): boolean {
    // Verificar tanto el estado interno como el estado real del elemento
    if (this.audio && !this.audio.paused) {
      return true;
    }
    return this.isPlaying;
  }
  
  // Método para forzar detención completa
  forceStopAlarm(): void {
    console.log('🛑 AUDIO: Forzando detención completa de alarma');
    
    // Detener todos los elementos de audio rastreados
    this.audioElements.forEach((audio, index) => {
      try {
        console.log(`🛑 AUDIO: Deteniendo elemento ${index + 1}`);
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
        audio.load();
        audio.remove();
      } catch (error) {
        console.error(`Error deteniendo elemento ${index + 1}:`, error);
      }
    });
    
    // Limpiar array
    this.audioElements = [];
    
    // Detener elemento principal
    if (this.audio) {
      try {
        this.audio.pause();
        this.audio.currentTime = 0;
        this.audio.volume = 0;
        this.audio.src = '';
        this.audio.load();
      } catch (error) {
        console.error('Error deteniendo audio principal:', error);
      }
    }
    
    this.isPlaying = false;
    this.playPromise = null;
    this.audio = null;
    
    // Detener TODOS los elementos de audio en la página
    this.stopAllAudioElements();
    
    // Recrear elemento de audio después de un delay
    setTimeout(() => {
      this.prepareAudio();
    }, 1000);
    
    console.log('✅ AUDIO: Detención forzada completada');
  }
  
  // Método nuclear para detener TODO el audio en la página
  private stopAllAudioElements(): void {
    console.log('💥 AUDIO: Deteniendo TODOS los elementos de audio en la página');
    
    // Obtener todos los elementos de audio en el DOM
    const allAudioElements = document.querySelectorAll('audio');
    allAudioElements.forEach((audio, index) => {
      try {
        console.log(`💥 AUDIO: Deteniendo elemento DOM ${index + 1}`);
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
        audio.load();
      } catch (error) {
        console.error(`Error deteniendo elemento DOM ${index + 1}:`, error);
      }
    });
    
    // También verificar elementos de video que puedan tener audio
    const allVideoElements = document.querySelectorAll('video');
    allVideoElements.forEach((video, index) => {
      try {
        if (!video.muted) {
          console.log(`💥 AUDIO: Deteniendo video ${index + 1}`);
          video.pause();
          video.currentTime = 0;
          video.volume = 0;
        }
      } catch (error) {
        console.error(`Error deteniendo video ${index + 1}:`, error);
      }
    });
  }
  
  // Método de emergencia absoluta
  emergencyStopAll(): void {
    console.log('🆘 EMERGENCY: Detención de emergencia de TODO el audio');
    
    // 1. Detener todo el audio HTML
    this.forceStopAlarm();
    
    // 2. Buscar y detener CUALQUIER elemento de audio en toda la página
    const allMedia = document.querySelectorAll('audio, video, embed, object');
    allMedia.forEach((element: any, index) => {
      try {
        console.log(`🆘 EMERGENCY: Deteniendo elemento multimedia ${index + 1}`);
        if (element.pause) element.pause();
        if (element.stop) element.stop();
        element.volume = 0;
        element.muted = true;
        if (element.src) element.src = '';
        if (element.load) element.load();
      } catch (error) {
        console.error(`Error deteniendo elemento ${index + 1}:`, error);
      }
    });
    
    // 3. Suspender Web Audio API
    try {
      if (window.AudioContext || (window as any).webkitAudioContext) {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        const audioContext = new AudioContext();
        audioContext.suspend().then(() => {
          console.log('🆘 EMERGENCY: AudioContext suspendido');
        });
      }
    } catch (error) {
      console.error('Error suspendiendo AudioContext:', error);
    }
    
    // 4. Detener cualquier Web Audio API activa
    try {
      (window as any).webkitAudioContext = null;
      (window as any).AudioContext = null;
    } catch (error) {
      console.error('Error limpiando AudioContext:', error);
    }
    
    // 5. Método nuclear: remover TODOS los elementos de audio del DOM
    setTimeout(() => {
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach((audio, index) => {
        try {
          console.log(`🆘 NUCLEAR: Removiendo elemento de audio ${index + 1}`);
          audio.remove();
        } catch (error) {
          console.error(`Error removiendo audio ${index + 1}:`, error);
        }
      });
    }, 500);
    
    // 6. Limpiar variables de estado
    this.isPlaying = false;
    this.audioReady = false;
    this.playPromise = null;
    this.audio = null;
    this.audioElements = [];
    
    console.log('✅ EMERGENCY: Detención de emergencia completada');
  }
}